import { useEffect, useState } from 'react';
import { publicRegistry } from '@/i18n/dictionaries/publicRegistry';
import { useTranslation } from '@/i18n/useTranslation';
import { libraryApi } from '@/services/libraryApi';
import type { SaveButtonProps } from '@/features/common/types/SaveButtonProps';

/**
 * "Salvează" on a library resource — the third of FR-021's three functions.
 *
 * The tender asks for "funcții de descărcare, salvare și citare automată"; download and citation
 * existed and this one was backed by nothing at all, so the card delivered two of three.
 *
 * Only rendered for a signed-in reader: a reading list belongs to somebody. The library itself
 * stays open to anonymous visitors, which is the point of the module.
 */
export function SaveButton({ resourceId, saved: savedNow, onChange }: SaveButtonProps) {
  const { t } = useTranslation(publicRegistry);
  const [saved, setSaved] = useState(savedNow);
  // The cards are drawn before the reader's saved ids arrive: the button takes the answer when it
  // does, or a resource already saved would offer "Salvează" until the page was reloaded.
  useEffect(() => setSaved(savedNow), [savedNow]);
  const [busy, setBusy] = useState(false);

  async function toggle() {
    const next = !saved;
    setBusy(true);
    // Optimistic, then corrected on failure: saving is a small, reversible act and the button
    // should feel immediate. A failure puts it back rather than leaving a lie on screen.
    setSaved(next);
    try {
      if (next) {
        await libraryApi.save(resourceId);
      } else {
        await libraryApi.unsave(resourceId);
      }
      onChange?.(next);
    } catch {
      setSaved(!next);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={saved ? 'btn btn--secondary' : 'btn btn--ghost'}
      aria-pressed={saved}
      disabled={busy}
      onClick={() => void toggle()}
    >
      {saved ? t('librarySaved') : t('librarySave')}
    </button>
  );
}
